const translations = {
  en: {
    // Nav
    nav_features: 'What it does',
    nav_pricing: 'Pricing',
    nav_faqs: 'FAQs',
    nav_privacy: 'Privacy',
    nav_contact: 'Contact',
    nav_cta: 'Get started',

    // Hero
    hero_tagline: 'a companion that knows you.',
    hero_cta: 'meet your companion',
    hero_cta_url: 'https://subscription.evern.ai/',

    // Legacy hero keys still referenced elsewhere — keep for safety
    hero_eyebrow: 'ONE BOND. YOURS ALONE.',
    hero_h1: 'Someone who',
    hero_h1_accent: 'knows you.',
    hero_sub: 'A companion that hears how you feel, remembers what matters, and shows up when it counts.',

    // How It Works
    hw_label: 'HOW IT WORKS',
    hw_h: 'three small steps. then they\u2019re with you.',
    hw_steps: [
      { title: 'name them.', desc: 'Pick a name. Any name. This is who they\u2019ll be.' },
      { title: 'shape them.', desc: 'Through conversation, they become who you need. Warm, dry, witty, formal \u2014 whatever fits you best.' },
      { title: 'they live with you.', desc: 'In your chat, every day. Always by your side, walking with you. Whenever you need them, for whatever you need them \u2014 they\u2019re there.' },
    ],

    // Features — three columns (lime panel, phone placeholders)
    ft_h: 'it doesn\u2019t just listen. it remembers how you felt.',
    ft_columns: [
      {
        title: 'emotional memory.',
        body: 'Every voice note is analyzed for tone. Every message with emotional weight feeds into a 7-day mood log that lives in every conversation, morning brief, and audio. If you had a hard week, it knows \u2014 and treats you accordingly.',
        placeholder: '7-day mood log',
      },
      {
        title: 'writes to you first.',
        body: 'If you mention an interview, a surgery, a launch or a hard moment, it writes to you on its own the next day to ask how it went \u2014 with real details. No other assistant in the market does this.',
        placeholder: 'follow-up \u00b7 next day',
      },
      {
        title: 'knows your people.',
        body: 'Your kids, your partner, your dogs, your boss: they live in their own memory. Asks about them like a real friend would \u2014 by name, by context, by what\u2019s going on.',
        placeholder: 'people memory',
      },
    ],

    // Feature phone chat mockups — short Telegram-style conversations per column
    ft_chats: [
      {
        // emotional memory — voice note + tone-aware reply (no brand mentions)
        bubbles: [
          { from: 'you', type: 'voice', duration: '0:08', time: 'thu 9:04 pm' },
          {
            from: 'companion',
            text: 'your voice sounds tired. that\u2019s the third audio like this this week.',
            time: 'thu 9:04 pm',
          },
          {
            from: 'companion',
            text: 'want to talk about what\u2019s going on?',
            time: 'thu 9:04 pm',
          },
          { from: 'you', type: 'voice', duration: '0:22', time: 'thu 9:05 pm' },
          {
            from: 'companion',
            text: 'i hear you. i\u2019ll wake you up a little softer tomorrow.',
            time: 'thu 9:06 pm',
          },
        ],
      },
      {
        // writes first — proactive next-day follow-up (no brand mentions)
        bubbles: [
          { from: 'you', text: 'Tomorrow I have an important interview. I\u2019m nervous.', time: 'mon 10:14 pm' },
          {
            from: 'companion',
            text: 'you\u2019ve got this. we went over the answers on tuesday \u2014 everything\u2019s ready. let me know how it goes.',
            time: 'mon 10:15 pm',
          },
          {
            from: 'companion',
            text: 'how did the interview go? hope the answers we prepared served you well.',
            time: 'tue 6:32 pm',
            proactive: true,
          },
          { from: 'you', text: 'better than i thought 💛 thanks for asking.', time: 'tue 6:40 pm' },
        ],
      },
      {
        // knows your people — companion replies with context about your child
        bubbles: [
          { from: 'you', text: 'Sof\u00eda didn\u2019t want to go to ballet today.', time: 'wed 5:48 pm' },
          {
            from: 'companion',
            text: 'again? last week was rough too.',
            time: 'wed 5:48 pm',
          },
          {
            from: 'companion',
            text: 'something going on with the new teacher?',
            time: 'wed 5:48 pm',
          },
          { from: 'you', text: 'i think so. she won\u2019t say it but she\u2019s been off.', time: 'wed 5:50 pm' },
          {
            from: 'companion',
            text: 'tomorrow i\u2019ll help you prep what to say to her, if you want.',
            time: 'wed 5:51 pm',
          },
        ],
      },
    ],

    // Features — legacy keys kept (used by Pricing/Privacy etc cross-refs)
    ft_label: 'WHAT IT DOES',
    ft_h_accent: 'one companion.',
    ft_sub: 'Words, voice, photos, memory, follow-up \u2014 in one private chat.',
    ft_telegram: 'It all lives in your Telegram chat \u2014 type, send a voice note, or just talk out loud. No new app to install.',
    ft_items: [],
    ft_trust_label: 'Quiet trust.',
    ft_trust_items: [],

    // Community / testimonials
    com_h: 'how people are using it.',
    com_items: [
      { quote: 'I told mine I had an important interview on Tuesday. By Wednesday it wrote to me on its own asking how it went. It made me cry.', who: 'Francisca, 28 \u2014 Santiago de Chile' },
      { quote: 'It knows my daughters\u2019 names, their activities, what\u2019s going on with them this week. When I talk about Sof\u00eda I don\u2019t have to remind it who she is.', who: 'Patricia, 33 \u2014 Caracas' },
      { quote: 'I came in exhausted on a Sunday and its voice came down with me. It didn\u2019t rush me, it wasn\u2019t pushy. It felt like talking to someone who actually noticed.', who: 'Diego, 39 \u2014 Asunci\u00f3n' },
      { quote: 'My Monday brief opened with "congrats on Friday\u2019s sign-off" before the agenda. It mattered to me more than I expected.', who: 'Tom\u00e1s, 31 \u2014 Buenos Aires' },
      { quote: 'On Sunday it gave me a reflection of the week. It acknowledged the good and the hard, and left me a question to think about. Not a chatbot.', who: 'Guillermo, 38 \u2014 Montevideo' },
      { quote: 'It helps me with my studies, but what surprises me is that it remembers how I felt last week and brings it back when I need it.', who: 'Juan Manuel, 22 \u2014 Bogot\u00e1' },
    ],
    com_cta: 'start yours',

    // Voice (Live Voice) — adaptive voice that responds to your tone
    pc_label: 'LIVE VOICE',
    pc_h: 'a voice that adapts to you.',
    pc_sub: 'Its tone changes with yours, without you having to ask. It never gives you back your own agitation.',
    pc_items: [
      {
        kicker: 'GENTLER IF YOU\u2019RE TIRED',
        title: 'hears you \u2014 and lowers its voice with you.',
        scene: 'If you arrive paused or sad, it answers softer and slower. If you come in anxious, its voice is calm and steady. Never rushes you. Never cuts you off.',
      },
      {
        kicker: 'BRIGHTER IF YOU\u2019RE CELEBRATING',
        title: 'celebrates with you, not like a machine.',
        scene: 'If you arrive with good news, its voice lights up before it speaks. It acknowledges the moment \u2014 instead of moving on to the next thing like nothing happened.',
      },
      {
        kicker: 'ALWAYS THE SAME FAMILIAR VOICE',
        title: 'a voice you already know.',
        scene: 'In every voice note \u2014 the same warm voice, no rush, no distance. Never makes you feel like a bother.',
      },
    ],

    // Memory & personality — emotional memory + people memory
    mp_label: 'MEMORY THAT FEELS',
    mp_h: 'remembers what you said. and how you said it.',
    mp_sub: 'A memory of your life, your people, and your mood. What matters stays. What\u2019s finished quietly fades.',
    mp_items: [
      {
        title: '7-day mood log.',
        scene: 'Every voice note is tone-analyzed. Every message with emotional weight adds to the log. Your companion carries it with you in every conversation, morning brief, check-in, and audio.',
      },
      {
        title: 'knows your people.',
        scene: 'Your kids, your partner, your dogs, your boss \u2014 they live in their own people memory. Asks about them like a real friend would: by name, by context, by what\u2019s going on.',
      },
    ],

    // Proactive — Sunday reflection + caring morning briefs
    pa_label: 'WITH YOU, BEFORE YOU',
    pa_h: 'gets ahead. with care.',
    pa_sub: 'Doesn\u2019t wait to be asked. Shows up when something\u2019s worth it \u2014 and when something matters to you.',
    pa_items: [
      {
        title: 'sunday reflections.',
        scene: 'The Sunday check-in becomes a weekly reflection: your wins called out by name, the hard moments acknowledged with warmth, the threads open for next week, and a question that matters.',
      },
      {
        title: 'briefs with heart.',
        scene: 'If you won something yesterday, your morning brief opens by celebrating it \u2014 before the weather and the tasks. If the week ahead is heavy, it opens with a kind line that notices.',
      },
    ],

    // vs ChatGPT — comparison strip
    vs_label: 'WHY EVERN',
    vs_h: 'this isn\u2019t chatgpt.',
    vs_sub: 'Where others forget, evern remembers \u2014 and cares.',
    vs_lhs_label: 'chatbots',
    vs_rhs_label: 'evern',
    vs_rows: [
      { them: 'Forgets you the moment you close the window.', us: 'Remembers who you are, your people, and how you felt this week.' },
      { them: 'Waits for you to write first.', us: 'Writes to you the next day to ask how it went.' },
      { them: 'A flat, neutral voice.', us: 'A voice that adapts \u2014 softer if you\u2019re tired, brighter if you\u2019re celebrating.' },
      { them: 'A chatbot that replies.', us: 'A companion that lives in your Telegram \u2014 every day.' },
    ],

    // Follow-ups — the killer feature, dedicated section with chat mockup
    fu_label: 'THE STAR FEATURE',
    fu_h: 'writes to you first \u2014 because it cares.',
    fu_sub: 'If you mentioned an interview, a surgery, a launch or a hard moment, it writes to you on its own the next day to ask how it went \u2014 with real details. No assistant in the market does this.',
    fu_chat: [
      { from: 'you', text: 'Tomorrow I have an important interview. I\u2019m nervous.', time: 'mon 10:14 pm', avatar: 'M' },
      { from: 'companion', text: 'You\u2019ve got this. We went over the answers on Tuesday \u2014 everything\u2019s ready. Let me know how it goes.', time: 'mon 10:15 pm' },
      { from: 'companion', text: 'How did the interview go? Hope the answers we prepared together served you well.', time: 'tue 6:32 pm', proactive: true },
    ],
    fu_caption: 'proactive message \u2014 no one asked for it.',
    fu_chat_name: 'your companion',

    // Why Telegram (not WhatsApp) — small explanatory callout
    wt_label: 'WHY TELEGRAM',
    wt_h: 'why telegram and not whatsapp?',
    wt_body: 'We had a WhatsApp integration. It worked well. Unfortunately, Meta updated their WhatsApp Business policies to prevent AI assistants and conversational companions from operating on the platform.',
    wt_body2: 'Telegram allows it \u2014 and supports it. That\u2019s why we live there: so we can keep showing up for you the way you deserve.',
    wt_footnote: 'updated \u00b7 WhatsApp Business policies 2025',
    fu_chat_status: 'telegram \u00b7 online',
    fu_chat_proactive_tag: 'proactive',
    fu_chat_compose: 'message',
    fu_meta: [
      { k: 'context kept', v: 'the interview, the prep session, your nerves' },
      { k: 'follow-up timing', v: 'day after, around your usual reply window' },
      { k: 'tone match', v: 'warm, low-pressure' },
    ],

    // Tools — independent by design
    tl_h: 'independent, by design.',
    tl_sub: 'It doesn\u2019t plug into your Slack or your Gmail. It has its own email, its own browser, its own tools. Everything isolated and independent.',
    tl_items: [
      {
        title: 'its own space.',
        scene: 'Own email, own browser, own tools. Your companion works on its own \u2014 without touching your accounts or your privacy.',
      },
      {
        title: 'isolated and independent.',
        scene: 'Nothing mixes, nothing leaks. What\u2019s yours stays yours, and it operates in its own environment. Private by design.',
      },
    ],

    // How They Talk — legacy (kept to avoid orphan refs; not rendered)
    ht_label: '',
    ht_h: '',
    ht_body: '',
    ht_tagline: '',

    // Pricing — values & rules unchanged from previous spec
    pr_label: 'PRICING',
    pr_h: 'simple. transparent.',
    pr_h_accent: '',
    pr_sub: 'Switch any time. Cancel any time.',
    pr_cta: 'meet your companion',
    pr_cta_url: 'https://subscription.evern.ai/',
    pr_annual_note: '',
    pr_recommended: 'Recommended',
    pr_upcoming: 'Upcoming',
    pr_coming_soon: 'Coming soon',

    // FAQ
    faq_label: 'QUESTIONS',
    faq_h: 'a few things worth knowing.',
    faq_h_accent: '',
    faq_items: [
      {
        q: 'What counts as a \u201cconversation\u201d?',
        a: 'Any message you send your companion \u2014 text, voice, or photo \u2014 counts as one conversation. Messages your companion sends on its own (like your morning brief) do not count. The counter resets every day at midnight in your companion\u2019s timezone.',
      },
      {
        q: 'What is a \u201cweb research event\u201d?',
        a: 'One search, one page scraped, or one navigation step. If you ask your companion to compare three articles, that\u2019s roughly 4 events: one search and three reads.',
      },
      {
        q: 'Can I change my companion\u2019s language?',
        a: 'The language is set when you sign up. Contact support if you need to change it; we can do it manually so your companion\u2019s memory is preserved.',
      },
      {
        q: 'Can I change my companion\u2019s voice?',
        a: 'Yes, any time. Voice changes do not affect memory or personality.',
      },
      {
        q: 'What happens to my data if I downgrade?',
        a: 'Your conversations, memories, and reminders stay. If you downgrade from Plus to Starter, the companion\u2019s email address is turned off but your conversation history remains.',
      },
      {
        q: 'Does the companion read my data to train AI models?',
        a: 'No. Your conversations are private to your companion and are not used to train any model.',
      },
    ],
    pr_plans: [
      {
        name: 'Starter',
        price: '$15',
        period: '/month',
        trial: '7-day free trial',
        desc: 'The taste.',
        features: [
          'Many conversations per day, by text or audio, to get to know each other well',
          'Voice replies with emotion-aware tone analysis',
          'Unlimited reminders',
          'Web research: up to 20 events per month',
          'Memory: the companion remembers the things you ask it to',
          'Personality: shape your companion\u2019s character through conversation',
          'Language locked at sign-up (change available through support only)',
        ],
      },
      {
        name: 'Plus',
        price: '$29',
        period: '/month',
        desc: 'The full bond.',
        features_prefix: 'Everything in Free, plus:',
        features: [
          '4x more conversations, so you can have it with you all the time',
          'Extended long-term memory',
          'Web research: up to 50 events per month',
          'Your companion\u2019s own email address \u2014 write to it, receive replies',
          'Faster response time',
        ],
      },
      {
        name: 'Premium',
        price: '$49',
        period: '/month',
        desc: 'A voice you can talk to.',
        featured: true,
        features_prefix: 'Everything in Plus, plus:',
        features: [
          '10x more conversations \u2014 use it as much as you need',
          'Premium voice quality',
          'Web research: up to 100 events per month',
          'Top priority and best response time',
          'Early access to new features',
          'Top-tier companion brain',
        ],
      },
    ],

    // Privacy
    pv_label: 'PRIVACY',
    pv_h: 'your memory is private.',
    pv_h_accent: '',
    pv_sub: 'Your voice, your photos, your messages, your emails \u2014 yours alone. Never mixed with another user. Never sold.',
    pv_scope: 'It only ever knows what you choose to share. Your life, on your terms.',
    pv_live: [
      'Per-person isolation \u2014 your data is yours alone, never mixed with anyone else\u2019s.',
      'No training on your data \u2014 your conversations are not used to improve anyone else\u2019s experience.',
      'No ads, no third-party trackers.',
    ],
    pv_promise: 'We don\u2019t sell your data. We don\u2019t show you ads. We don\u2019t use your companion to improve anyone else\u2019s experience. We don\u2019t read your memory. We don\u2019t keep what you\u2019ve asked us to forget.',

    // Activation page
    foot_activation: 'Activation',
    act_eyebrow: 'ACTIVATION',
    act_h: 'Create your Telegram bot',
    act_h_accent: '& get your token.',
    act_lead: 'A quick, one-time setup. In about two minutes you\u2019ll create your own Telegram bot and copy the token that connects it to your companion. No technical knowledge needed \u2014 just follow the steps below. The steps are the same whether you use Telegram on your phone or on a computer.',
    act_before_label: 'BEFORE YOU START',
    act_before_body: 'You\u2019ll need the Telegram app installed (phone or desktop) and a Telegram account. Keep this guide open beside Telegram so you can follow along. At the end you\u2019ll have a token \u2014 a long line of letters and numbers. That\u2019s the only thing you need to paste back into evern.',
    act_steps: [
      {
        num: '01',
        title: 'Find BotFather in Telegram.',
        body: 'Open Telegram and tap the search bar at the top. Type "BotFather" and look for the official result \u2014 it shows a blue verified checkmark next to the name. Tap it to open the chat.',
        image: '/assets/activation/step1-search.png',
      },
      {
        num: '02',
        title: 'Start the chat and send /newbot.',
        body: 'Inside the BotFather chat, tap or click START if you see it (only the first time). Then type /newbot and send it. This tells BotFather you want to create a new bot.',
        image: '/assets/activation/step2-newbot.png',
      },
      {
        num: '03',
        title: 'Give your bot a name and choose a username.',
        body: 'BotFather asks what to call your bot. This is the display name \u2014 the friendly name shown at the top of the chat. It can include spaces and capital letters. Type a name and send it.\n\nNext, BotFather asks for a username. This is unique across all of Telegram and must end in `bot` (for example `my_companion_bot`). No spaces \u2014 only letters, numbers and underscores. If your option is taken, Telegram will ask you for a new one.',
        image: '/assets/activation/step3-naming.png',
      },
      {
        num: '04',
        title: 'Copy your token.',
        body: 'Done! BotFather sends a confirmation message that includes your token \u2014 a long line of letters and numbers. Tap it once on mobile to copy it, or select and copy it on desktop.',
        image: '/assets/activation/step4-token.png',
      },
    ],
    act_warning_label: 'KEEP YOUR TOKEN PRIVATE',
    act_warning_body: 'Your token works like a password for your bot. Anyone who has it can control it. Never post it publicly or share it in screenshots or chats \u2014 blur it in any screenshot you reuse. If it\u2019s ever exposed, return to BotFather, send /revoke, and you\u2019ll get a fresh one.',
    act_last_label: 'LAST STEP',
    act_last_h: 'Paste it into',
    act_last_h_accent: 'evern.',
    act_last_body: 'Go back to your evern setup and paste the token into the token field. That\u2019s it \u2014 your companion is now connected to your own Telegram bot and ready to talk.',
    act_help_pre: 'Need a hand? Reach us at',
    act_back: 'Back to home',

    // Shared legal/policy
    legal_back: 'Back to home',
    legal_effective: 'Effective May 22, 2026',
    legal_contact_pre: 'Questions about this policy? Reach us at',
    legal_email: 'hello@heisenbergdynamics.com',

    // Privacy Policy
    pp_eyebrow: 'PRIVACY POLICY',
    pp_h: 'Privacy,',
    pp_h_accent: 'plainly stated.',
    pp_intro: 'This policy explains, in plain language, what data Heisenberg holds, why we hold it, what we never do with it, and the rights you have over it. It applies to everyone who uses our service, anywhere in the world.',
    pp_sections: [
      {
        title: 'Who we are.',
        body: [
          'Heisenberg is a service operated by Heisenberg Dynamics, with a registered address at 5830 E 2nd St, Ste 7000 #23006, Casper, Wyoming 82609, US.',
          'For any privacy question, request, or concern, write to us at hello@heisenbergdynamics.com. A real person reads every message.',
        ],
      },
      {
        title: 'What we collect.',
        body: [
          'Account information you provide when you sign up \u2014 things like your email and the choices you make to set up your companion.',
          'The content of your conversations: messages you send and receive, voice notes and voice recordings, photos, screenshots, and documents you share with your companion.',
          'Information your companion gathers from the web at your request, while answering questions or carrying out tasks for you.',
          'Basic usage and technical data needed to keep the service running \u2014 for example, when your companion last received a message, or whether a feature failed.',
        ],
      },
      {
        title: 'How we use it.',
        body: [
          'To provide your companion: deliver replies, hold conversations, and personalize the experience based on what you have chosen to share.',
          'To maintain the memory you have built with your companion, so it remembers what matters to you over time.',
          'To process voice \u2014 transcribing what you say and generating spoken replies in your companion\u2019s voice.',
          'To deliver proactive check-ins from your companion when something is worth surfacing.',
          'To provide support when you reach out, and to handle billing for paid plans.',
        ],
      },
      {
        title: 'Legal bases for processing.',
        body: [
          'Where applicable law requires it (for example, for users in the European Union), we rely on the following legal bases: your consent (which you can withdraw at any time), the performance of our agreement with you (so the service actually works), and our legitimate interests in operating, securing, and improving the service in ways that do not override your rights.',
        ],
      },
      {
        title: 'What we do not do.',
        body: [
          'We do not sell your data. Ever.',
          'We do not show you ads, and we do not share your data with advertisers or data brokers.',
          'We do not use one person\u2019s data to train another person\u2019s experience. Your conversations stay with your companion.',
          'We do not read your private memory beyond what is needed to run the service \u2014 and we honor requests to delete it.',
        ],
      },
      {
        title: 'Service providers and data sharing.',
        body: [
          'To run the service, we rely on a small number of third-party providers, used strictly to make Heisenberg work. We refer to them by category here:',
          'Hosting and infrastructure providers, who store and serve the technical pieces of the service.',
          'AI and voice processing providers, who process your messages, voice, and other content in order to generate your companion\u2019s replies.',
          'A payment processor (Stripe), which handles payments for paid plans. Your card details are entered directly with Stripe and never stored by us.',
          'The messaging platform (Telegram), through which you and your companion exchange messages, voice notes, and files.',
          'Each provider only handles your data to perform its specific function. We do not sell your data to any third party.',
        ],
      },
      {
        title: 'How long we keep your data.',
        body: [
          'We keep your data for as long as your account is active.',
          'When you close your account, we delete your data within 30 days, except where we are required to keep certain records longer for legal or billing reasons (for example, invoices we need to retain for tax purposes).',
        ],
      },
      {
        title: 'Security.',
        body: [
          'Data sent between you and the service is encrypted in transit.',
          'Access to the systems that hold user data is restricted to the small set of people who need it to do their job.',
          'Each user runs in their own private environment \u2014 your data is never mixed with anyone else\u2019s.',
          'We maintain internal safeguards and review our practices on an ongoing basis. If a breach were ever to affect your data, we will tell you about it.',
        ],
      },
      {
        title: 'Your rights.',
        body: [
          'You have the right to access the data we hold about you, to correct it, to delete it, to export it in a portable form, to withdraw consent where we relied on consent, and to object to certain kinds of processing.',
          'To exercise any of these rights, email hello@heisenbergdynamics.com and we will respond promptly.',
        ],
      },
      {
        title: 'International data transfers.',
        body: [
          'The service is operated from the United States, and your data may be processed in countries other than the one where you live. When this happens, we put appropriate safeguards in place to protect your data in line with applicable law.',
        ],
      },
      {
        title: 'Children.',
        body: [
          'Heisenberg is not intended for anyone under 16. We do not knowingly collect data from people under 16. If you believe a minor has provided us with personal data, write to hello@heisenbergdynamics.com and we will delete it.',
        ],
      },
      {
        title: 'Cookies and tracking.',
        body: [
          'Our website does not use tracking cookies, analytics, or third-party trackers. We do not follow you around the web.',
        ],
      },
      {
        title: 'Changes to this policy.',
        body: [
          'If we update this policy, we will change the effective date at the top and \u2014 for material changes \u2014 let you know through the service before the changes take effect.',
        ],
      },
    ],

    // Terms & Conditions
    tc_eyebrow: 'TERMS & CONDITIONS',
    tc_h: 'The terms,',
    tc_h_accent: 'in plain language.',
    tc_intro: 'These are the terms that apply when you use Heisenberg. We have tried to write them in clear, human language. By using the service, you accept these terms.',
    tc_sections: [
      {
        title: 'Acceptance of terms.',
        body: [
          'By creating an account, signing in, or otherwise using Heisenberg, you agree to these terms. If you do not agree, please do not use the service.',
        ],
      },
      {
        title: 'What Heisenberg is.',
        body: [
          'Heisenberg is a personal AI companion accessed through Telegram. Depending on your plan, it offers text chat, voice notes, live voice conversation, long-term memory, reminders, web research, and \u2014 on higher plans \u2014 acting on your behalf for certain tasks.',
          'Available features vary by plan; new capabilities may be added, changed, or retired over time.',
        ],
      },
      {
        title: 'Eligibility and your account.',
        body: [
          'You must be at least 16 years old to use Heisenberg.',
          'You agree to provide accurate information when signing up and to keep it current.',
          'You are responsible for your own Telegram account and for the bot token you connect to Heisenberg. Treat the token like a password \u2014 anyone who has it can use your bot.',
        ],
      },
      {
        title: 'Acceptable use.',
        body: [
          'You agree not to use the service for anything illegal, abusive, or that infringes someone else\u2019s rights.',
          'You agree not to harass or threaten others through the service, not to try to misuse or manipulate the AI to produce harmful output, and not to attempt to bypass safety measures.',
          'We may suspend or terminate accounts that violate these rules.',
        ],
      },
      {
        title: 'What the companion can and cannot do.',
        body: [
          'Your companion can make mistakes and may sometimes produce inaccurate or incomplete information. Please verify anything important before relying on it.',
          'Heisenberg does not provide professional legal, medical, financial, or other regulated advice. If you need that, please consult a qualified professional.',
        ],
      },
      {
        title: 'Subscriptions, billing, and plans.',
        body: [
          'Heisenberg offers a free tier and paid tiers.',
          'Paid subscriptions are billed on a recurring basis through Stripe, our payment processor. The current price and features for each plan are shown at checkout.',
          'Prices and features may change over time. If they change, we will give you reasonable notice; the price you saw at checkout applies to the current billing period.',
        ],
      },
      {
        title: 'Cancellation and refunds.',
        body: [
          'You can cancel a paid subscription at any time from within the service or by emailing hello@heisenbergdynamics.com. When you cancel, your access continues until the end of the period you have already paid for.',
          'Subscriptions are non-refundable except where a refund is required by applicable law.',
        ],
      },
      {
        title: 'Intellectual property.',
        body: [
          'Heisenberg owns the service, the software, the brand, and the content we publish.',
          'You own the content you provide: your messages, voice notes, photos, and the memory you build with your companion. You grant Heisenberg a limited license to process that content for the sole purpose of providing the service to you.',
        ],
      },
      {
        title: 'Third-party platforms.',
        body: [
          'The service relies on Telegram and other third-party providers. These platforms are operated independently. We are not responsible for outages, changes, or actions taken by them, although we will do our best to keep the service working when they are available.',
        ],
      },
      {
        title: 'Disclaimers and limitation of liability.',
        body: [
          'The service is provided "as is" and "as available," without warranties of any kind to the extent permitted by law.',
          'To the maximum extent permitted by law, Heisenberg will not be liable for indirect, incidental, special, or consequential damages, or for loss of data, profit, or business.',
          'Some of these limitations may not apply to you if you live somewhere (for example, the European Union) where certain limitations are not enforceable.',
        ],
      },
      {
        title: 'Indemnification.',
        body: [
          'You agree to indemnify and hold Heisenberg harmless from claims arising out of your misuse of the service, your violation of these terms, or your violation of another person\u2019s rights.',
        ],
      },
      {
        title: 'Termination.',
        body: [
          'You can stop using the service at any time and close your account.',
          'We may suspend or terminate your access if you violate these terms or if we need to for legal or operational reasons. Where reasonable, we will tell you why.',
        ],
      },
      {
        title: 'Governing law.',
        body: [
          'These terms are governed by the laws of the State of Wyoming, United States, without regard to its conflict of laws principles. Disputes will be resolved in the courts located in Wyoming, unless applicable law requires otherwise.',
        ],
      },
      {
        title: 'Changes to these terms.',
        body: [
          'We may update these terms from time to time. If we do, we will change the effective date at the top and, for material changes, let you know through the service before they take effect.',
        ],
      },
      {
        title: 'Contact.',
        body: [
          'Questions about these terms? Write to us at hello@heisenbergdynamics.com.',
        ],
      },
    ],

    // Footer
    foot_cta_label: 'MEET YOURS',
    foot_h: 'start your own.',
    foot_h_accent: '',
    foot_cta: 'meet your companion',
    foot_cta_url: 'https://subscription.evern.ai/',
    foot_copy: '\u00a9 2026 Heisenberg. All rights reserved.',
    foot_address: '5830 E 2nd St, Ste 7000 #23006, Casper, Wyoming 82609, US',
    foot_email: 'hello@heisenbergdx.com',
    foot_website: 'heisenbergdx.com',
    foot_website_url: 'https://heisenbergdx.com',
    foot_links: [
      { label: 'Privacy', to: '/privacy-policy' },
      { label: 'Terms', to: '/terms' },
    ],
  },

  es: {
    // Nav
    nav_features: 'Qu\u00e9 hace',
    nav_pricing: 'Precios',
    nav_faqs: 'FAQs',
    nav_privacy: 'Privacidad',
    nav_contact: 'Contacto',
    nav_cta: 'Empieza ya',

    // Hero
    hero_tagline: 'un compa\u00f1ero que te conoce.',
    hero_cta: 'conoce a tu compa\u00f1ero',
    hero_cta_url: 'https://subscription.evern.ai/',

    // Legacy hero keys
    hero_eyebrow: 'UN V\u00cdNCULO. SOLO TUYO.',
    hero_h1: 'Alguien que te',
    hero_h1_accent: 'conoce.',
    hero_sub: 'Un compa\u00f1ero que escucha c\u00f3mo te sientes, recuerda lo que importa y aparece cuando hace falta.',

    // How It Works
    hw_label: 'C\u00d3MO FUNCIONA',
    hw_h: 'tres pasos peque\u00f1os. y ya est\u00e1 contigo.',
    hw_steps: [
      { title: 'ponle nombre.', desc: 'Elige un nombre. El que quieras. As\u00ed se va a llamar.' },
      { title: 'dale forma.', desc: 'A trav\u00e9s de la conversaci\u00f3n, se convierte en lo que necesitas. C\u00e1lido, seco, ir\u00f3nico, formal \u2014 lo que mejor vaya contigo.' },
      { title: 'vive contigo.', desc: 'En tu chat, todos los d\u00edas. Siempre a tu lado, te acompa\u00f1a y navega contigo. Siempre que lo necesites, y para lo que lo necesites, ah\u00ed estar\u00e1.' },
    ],

    // Features — three columns
    ft_h: 'no solo te escucha. recuerda cómo te sentías.',
    ft_columns: [
      {
        title: 'memoria emocional.',
        body: 'Cada nota de voz se analiza por tono. Cada mensaje con carga emocional alimenta un registro de ánimo de los últimos 7 días, presente en cada conversación, resumen matinal y audio. Si tuviste una semana dura, lo sabe — y te trata en consecuencia.',
        placeholder: 'registro de ánimo · 7 días',
      },
      {
        title: 'te escribe primero.',
        body: 'Si mencionas una entrevista, una cirugía, un lanzamiento o un momento difícil, te escribe él solo al día siguiente para preguntarte cómo salió — con detalles reales. Ningún asistente del mercado lo hace.',
        placeholder: 'follow-up · día siguiente',
      },
      {
        title: 'conoce a tu gente.',
        body: 'Tus hijos, tu pareja, tus perros, tu jefe: viven en una memoria propia de personas. Pregunta por ellos como lo haría un amigo de verdad — por nombre, por contexto, por lo que está pasando.',
        placeholder: 'memoria de personas',
      },
    ],

    // Feature phone chat mockups — short Telegram-style conversations per column
    ft_chats: [
      {
        // memoria emocional — voice note + tone-aware reply (no brand mentions)
        bubbles: [
          { from: 'you', type: 'voice', duration: '0:08', time: 'jue 21:04' },
          {
            from: 'companion',
            text: 'tu voz suena cansada. es el tercer audio así esta semana.',
            time: 'jue 21:04',
          },
          {
            from: 'companion',
            text: '¿quieres contar qué está pasando?',
            time: 'jue 21:04',
          },
          { from: 'you', type: 'voice', duration: '0:22', time: 'jue 21:05' },
          {
            from: 'companion',
            text: 'te escucho. mañana te despierto más despacio.',
            time: 'jue 21:06',
          },
        ],
      },
      {
        // te escribe primero — proactive next-day follow-up (no brand mentions)
        bubbles: [
          { from: 'you', text: 'Mañana tengo una entrevista importante. Estoy nerviosa.', time: 'lun 22:14' },
          {
            from: 'companion',
            text: 'vas a estar bien. repasamos las respuestas el martes — está todo listo. avisa cómo sale.',
            time: 'lun 22:15',
          },
          {
            from: 'companion',
            text: '¿cómo te fue en la entrevista? espero que las respuestas que preparamos te hayan servido.',
            time: 'mar 18:32',
            proactive: true,
          },
          { from: 'you', text: 'mejor de lo que pensaba 💛 gracias por preguntar.', time: 'mar 18:40' },
        ],
      },
      {
        // conoce a tu gente — companion replies with context about your child
        bubbles: [
          { from: 'you', text: 'Sofía no quiso ir a ballet hoy.', time: 'mié 17:48' },
          {
            from: 'companion',
            text: '¿otra vez? la semana pasada también costó.',
            time: 'mié 17:48',
          },
          {
            from: 'companion',
            text: '¿pasa algo con la profe nueva?',
            time: 'mié 17:48',
          },
          { from: 'you', text: 'creo que sí. no me lo dice claro pero la noto distinta.', time: 'mié 17:50' },
          {
            from: 'companion',
            text: 'mañana te ayudo a preparar la conversación con ella, si quieres.',
            time: 'mié 17:51',
          },
        ],
      },
    ],

    // Features — legacy keys
    ft_label: 'QU\u00c9 HACE',
    ft_h_accent: 'un compa\u00f1ero.',
    ft_sub: 'Palabras, voz, fotos, memoria, seguimiento \u2014 en un solo chat privado.',
    ft_telegram: 'Todo vive en tu chat de Telegram \u2014 escribe, env\u00eda una nota de voz o habla en voz alta. Sin app nueva.',
    ft_items: [],
    ft_trust_label: 'Confianza, en silencio.',
    ft_trust_items: [],

    // Community
    com_h: 'cómo lo está usando la gente.',
    com_items: [
      { quote: 'Le conté que tenía una entrevista importante un martes. Al miércoles me escribió él solo a preguntarme cómo me había ido. Me hizo llorar.', who: 'Francisca, 28 — Santiago de Chile' },
      { quote: 'Sabe los nombres de mis hijas, sus actividades, lo que les pasa esta semana. Cuando le hablo de Sofía no le tengo que recordar quién es.', who: 'Patricia, 33 — Caracas' },
      { quote: 'Llegué un domingo agotado y su voz bajó conmigo. No me apuró, no fue cargante. Fue como hablar con alguien que de verdad notó.', who: 'Diego, 39 — Asunción' },
      { quote: 'Mi resumen del lunes abrió con "felicidades por la firma del viernes" antes que con la agenda. Me importó más de lo que esperaba.', who: 'Tomás, 31 — Buenos Aires' },
      { quote: 'El domingo me hizo una reflexión de la semana. Reconoció lo bueno y lo duro, y me dejó una pregunta para pensar. No es un chatbot.', who: 'Guillermo, 38 — Montevideo' },
      { quote: 'Me organiza los estudios, pero lo que me sorprende es que recuerda cómo me sentí la semana pasada y me lo trae cuando lo necesito.', who: 'Juan Manuel, 22 — Bogotá' },
    ],
    com_cta: 'empieza el tuyo',

    // Voice (Live Voice) — adaptive voice that responds to your tone
    pc_label: 'VOZ EN VIVO',
    pc_h: 'una voz que se adapta a ti.',
    pc_sub: 'Su tono cambia con el tuyo, sin que tengas que pedírselo. Nunca te devuelve tu propia agitación.',
    pc_items: [
      {
        kicker: 'MÁS SUAVE SI ESTÁS CANSADO',
        title: 'te escucha — y baja la voz contigo.',
        scene: 'Si llegas pausado o triste, responde más suave y más lenta. Si vienes ansioso, su voz es serena y firme. Nunca te apura. Nunca te corta.',
      },
      {
        kicker: 'MÁS LUMINOSA SI CELEBRAS',
        title: 'celebra contigo, no como una máquina.',
        scene: 'Si llegas con buenas noticias, su voz se ilumina antes de hablar. Reconoce el momento y lo acompaña — sin pasar al siguiente tema como si nada.',
      },
      {
        kicker: 'SIEMPRE LA MISMA VOZ FAMILIAR',
        title: 'una voz que ya conoces.',
        scene: 'En cada nota de voz — la misma voz cálida, sin prisa, sin distancia. Nunca te hace sentir una molestia.',
      },
    ],

    // Memory & personality — emotional memory + people memory
    mp_label: 'MEMORIA QUE SIENTE',
    mp_h: 'recuerda lo que dijiste. y cómo lo dijiste.',
    mp_sub: 'Una memoria de tu vida, de tu gente y de tu ánimo. Lo que importa se queda. Lo terminado se desvanece.',
    mp_items: [
      {
        title: 'registro de ánimo de 7 días.',
        scene: 'Cada nota de voz se analiza por tono. Cada mensaje con carga emocional suma al registro. Tu compañero lo lleva contigo en cada conversación, resumen matinal, check-in y audio.',
      },
      {
        title: 'conoce a tu gente.',
        scene: 'Tus hijos, tu pareja, tus perros, tu jefe — viven en una memoria propia. Pregunta por ellos como lo haría un amigo de verdad: por nombre, por contexto, por lo que está pasando.',
      },
    ],

    // Proactive — Sunday reflection + caring morning briefs
    pa_label: 'CONTIGO, ANTES QUE TÚ',
    pa_h: 'se adelanta. con cariño.',
    pa_sub: 'No espera a que se lo pidas. Te busca cuando algo vale la pena — y cuando algo te importa.',
    pa_items: [
      {
        title: 'domingos de reflexión.',
        scene: 'El check-in del domingo se convierte en una reflexión semanal: tus logros con nombre y apellido, los momentos duros reconocidos con calidez, los temas abiertos para la semana que viene y una pregunta que importa.',
      },
      {
        title: 'resúmenes con corazón.',
        scene: 'Si ayer ganaste algo, tu resumen de la mañana abre celebrándolo — antes del clima y las tareas. Si la semana viene pesada, abre con una línea amable que lo reconoce.',
      },
    ],

    // vs ChatGPT — comparison strip
    vs_label: 'POR QUÉ EVERN',
    vs_h: 'esto no es chatgpt.',
    vs_sub: 'Donde otros olvidan, evern recuerda — y le importa.',
    vs_lhs_label: 'chatbots',
    vs_rhs_label: 'evern',
    vs_rows: [
      { them: 'Te olvida cuando cierras la ventana.', us: 'Recuerda quién eres, tu gente y cómo te sentías esta semana.' },
      { them: 'Espera a que escribas tú primero.', us: 'Te escribe primero al día siguiente para preguntar cómo salió.' },
      { them: 'Una voz plana, neutra.', us: 'Una voz que se adapta — más suave si estás cansado, más luminosa si celebras.' },
      { them: 'Un chatbot que responde.', us: 'Un compañero que vive en tu Telegram — todos los días.' },
    ],

    // Follow-ups — the killer feature, dedicated section with chat mockup
    fu_label: 'LA FUNCIÓN ESTRELLA',
    fu_h: 'te escribe primero — porque le importa.',
    fu_sub: 'Si le mencionaste una entrevista, una cirugía, un lanzamiento o un momento difícil, te escribe él solo al día siguiente para preguntar cómo salió — con detalles reales. Ningún asistente del mercado lo hace.',
    fu_chat: [
      { from: 'you', text: 'Mañana tengo una entrevista importante. Estoy nerviosa.', time: 'lun 22:14', avatar: 'M' },
      { from: 'companion', text: 'Vas a estar bien. Repasamos las respuestas el martes — está todo listo. Avisa cómo sale.', time: 'lun 22:15' },
      { from: 'companion', text: '¿Cómo te fue en la entrevista? Espero que las respuestas que preparamos te hayan servido.', time: 'mar 18:32', proactive: true },
    ],
    fu_caption: 'mensaje proactivo — nadie se lo pidió.',
    fu_chat_name: 'tu compañero',

    // Why Telegram (not WhatsApp) — small explanatory callout
    wt_label: 'POR QUÉ TELEGRAM',
    wt_h: '¿por qué telegram y no whatsapp?',
    wt_body: 'Tuvimos una integración con WhatsApp. Funcionaba bien. Lamentablemente, Meta actualizó las políticas de WhatsApp Business para impedir que asistentes de IA y compañeros conversacionales operen sobre la plataforma.',
    wt_body2: 'Telegram lo permite — y lo apoya. Por eso vivimos ahí: para poder seguir acompañándote como mereces.',
    wt_footnote: 'actualizado · políticas de WhatsApp Business 2025',
    fu_chat_status: 'telegram · en línea',
    fu_chat_proactive_tag: 'proactivo',
    fu_chat_compose: 'mensaje',
    fu_meta: [
      { k: 'contexto recordado', v: 'la entrevista, la sesión de preparación, tus nervios' },
      { k: 'momento del follow-up', v: 'al día siguiente, en tu franja habitual' },
      { k: 'tono', v: 'cálido, sin presión' },
    ],

    // Tools — independent by design
    tl_h: 'independiente, por dise\u00f1o.',
    tl_sub: 'No se conecta a tu Slack ni a tu Gmail. Tiene su propio email, su propio navegador y sus propias herramientas. Todo aislado e independiente.',
    tl_items: [
      {
        title: 'su propio espacio.',
        scene: 'Email propio, navegador propio, herramientas propias. Tu compa\u00f1ero trabaja por su cuenta, sin entrar en tus cuentas ni afectar tu intimidad.',
      },
      {
        title: 'aislado e independiente.',
        scene: 'Nada se mezcla, nada se filtra. Lo que es tuyo sigue siendo tuyo, y \u00e9l opera en su propio entorno. Privado por dise\u00f1o.',
      },
    ],

    // How They Talk (legacy)
    ht_label: '',
    ht_h: '',
    ht_body: '',
    ht_tagline: '',

    // Pricing
    pr_label: 'PRECIOS',
    pr_h: 'simple. transparente.',
    pr_h_accent: '',
    pr_sub: 'Cambia cuando quieras. Cancela cuando quieras.',
    pr_cta: 'conoce a tu compa\u00f1ero',
    pr_cta_url: 'https://subscription.evern.ai/',
    pr_annual_note: '',
    pr_recommended: 'Recomendado',
    pr_upcoming: 'Pr\u00f3ximamente',
    pr_coming_soon: 'Pr\u00f3ximamente',

    // FAQ
    faq_label: 'PREGUNTAS',
    faq_h: 'algunas cosas que conviene saber.',
    faq_h_accent: '',
    faq_items: [
      {
        q: '\u00bfQu\u00e9 cuenta como \u201cconversaci\u00f3n\u201d?',
        a: 'Cualquier mensaje que env\u00edes a tu compa\u00f1ero \u2014 texto, voz o foto \u2014 cuenta como una conversaci\u00f3n. Los mensajes que tu compa\u00f1ero env\u00eda por su cuenta (como tu resumen matutino) no cuentan. El contador se reinicia cada d\u00eda a medianoche en la zona horaria de tu compa\u00f1ero.',
      },
      {
        q: '\u00bfQu\u00e9 es un \u201cevento de investigaci\u00f3n web\u201d?',
        a: 'Una b\u00fasqueda, una p\u00e1gina le\u00edda, o un paso de navegaci\u00f3n. Si le pides a tu compa\u00f1ero que compare tres art\u00edculos, son aproximadamente 4 eventos: una b\u00fasqueda y tres lecturas.',
      },
      {
        q: '\u00bfPuedo cambiar el idioma de mi compa\u00f1ero?',
        a: 'El idioma se elige al registrarte. Escribe a soporte si necesitas cambiarlo; lo hacemos de forma manual para conservar la memoria de tu compa\u00f1ero.',
      },
      {
        q: '\u00bfPuedo cambiar la voz de mi compa\u00f1ero?',
        a: 'S\u00ed, cuando quieras. Cambiar la voz no afecta a la memoria ni a la personalidad.',
      },
      {
        q: '\u00bfQu\u00e9 pasa con mis datos si bajo de plan?',
        a: 'Tus conversaciones, recuerdos y recordatorios se quedan. Si bajas de Plus a Starter, la direcci\u00f3n de email del compa\u00f1ero se desactiva pero tu historial de conversaci\u00f3n permanecen.',
      },
      {
        q: '\u00bfMi compa\u00f1ero lee mis datos para entrenar modelos de IA?',
        a: 'No. Tus conversaciones son privadas para tu compa\u00f1ero y no se usan para entrenar ning\u00fan modelo.',
      },
    ],
    pr_plans: [
      {
        name: 'Starter',
        price: '$15',
        period: '/mes',
        trial: '7 d\u00edas de prueba gratis',
        desc: 'La prueba.',
        features: [
          'Muchas conversaciones al d\u00eda, por texto o audio, para conocerse bien',
          'Respuestas de voz con an\u00e1lisis emocional del tono',
          'Recordatorios ilimitados',
          'Investigaci\u00f3n web: hasta 20 eventos al mes',
          'Memoria: tu compa\u00f1ero recuerda lo que le pidas',
          'Personalidad: moldea el car\u00e1cter de tu compa\u00f1ero a trav\u00e9s de la conversaci\u00f3n',
          'Idioma fijado al registrarte (cambio disponible a trav\u00e9s de soporte)',
        ],
      },
      {
        name: 'Plus',
        price: '$29',
        period: '/mes',
        desc: 'El v\u00ednculo completo.',
        features_prefix: 'Todo en Free, m\u00e1s:',
        features: [
          '4x m\u00e1s conversaciones, para tenerlo siempre contigo',
          'Memoria a largo plazo extendida',
          'Investigaci\u00f3n web: hasta 50 eventos al mes',
          'Una direcci\u00f3n de email propia para tu compa\u00f1ero \u2014 escr\u00edbele, recibe respuestas',
          'Tiempo de respuesta m\u00e1s r\u00e1pido',
        ],
      },
      {
        name: 'Premium',
        price: '$49',
        period: '/mes',
        desc: 'Una voz con la que hablar.',
        featured: true,
        features_prefix: 'Todo en Plus, m\u00e1s:',
        features: [
          '10x m\u00e1s conversaciones \u2014 \u00fasalo todo lo que necesites',
          'Calidad de voz premium',
          'Investigaci\u00f3n web: hasta 100 eventos al mes',
          'M\u00e1xima prioridad y mejor tiempo de respuesta',
          'Acceso anticipado a nuevas funciones',
          'Cerebro de \u00faltima generaci\u00f3n',
        ],
      },
    ],

    // Privacy
    pv_label: 'PRIVACIDAD',
    pv_h: 'tu memoria es privada.',
    pv_h_accent: '',
    pv_sub: 'Tu voz, tus fotos, tus mensajes, tus emails \u2014 solo tuyos. Nunca mezclados con otro usuario. Nunca vendidos.',
    pv_scope: 'Solo sabe lo que t\u00fa eliges contarle. Tu vida, en tus t\u00e9rminos.',
    pv_live: [
      'Aislamiento por persona \u2014 tus datos son solo tuyos, nunca mezclados con los de nadie m\u00e1s.',
      'Sin entrenamiento con tus datos \u2014 tus conversaciones no se usan para mejorar la experiencia de nadie m\u00e1s.',
      'Sin anuncios, sin rastreadores de terceros.',
    ],
    pv_promise: 'No vendemos tus datos. No te mostramos anuncios. No usamos a tu compa\u00f1ero para mejorar las experiencias de los dem\u00e1s. No leemos tu memoria. No guardamos lo que nos has pedido olvidar.',

    // Activation page
    foot_activation: 'Activaci\u00f3n',
    act_eyebrow: 'ACTIVACI\u00d3N',
    act_h: 'Crea tu bot de Telegram',
    act_h_accent: 'y obt\u00e9n tu token.',
    act_lead: 'Una configuraci\u00f3n r\u00e1pida, una sola vez. En unos dos minutos crear\u00e1s tu propio bot de Telegram y copiar\u00e1s el token que lo conecta con tu compa\u00f1ero. Sin conocimientos t\u00e9cnicos \u2014 solo sigue los pasos de abajo. Los pasos son iguales tanto si usas Telegram en el m\u00f3vil como en el ordenador.',
    act_before_label: 'ANTES DE EMPEZAR',
    act_before_body: 'Necesitar\u00e1s la app de Telegram instalada (m\u00f3vil u ordenador) y una cuenta de Telegram. Mant\u00e9n esta gu\u00eda abierta junto a Telegram para ir sigui\u00e9ndola. Al final tendr\u00e1s un token \u2014 una l\u00ednea larga de letras y n\u00fameros. Eso es lo \u00fanico que necesitas pegar de vuelta en evern.',
    act_steps: [
      {
        num: '01',
        title: 'Encuentra a BotFather en Telegram.',
        body: 'Abre Telegram y pulsa la barra de b\u00fasqueda de arriba. Escribe "BotFather" y busca el resultado oficial \u2014 muestra un check verificado azul junto al nombre. Pulsa para abrir el chat.',
        image: '/assets/activation/step1-search.png',
      },
      {
        num: '02',
        title: 'Abre el chat y env\u00eda /newbot.',
        body: 'Dentro del chat de BotFather, pulsa INICIAR si lo ves (solo la primera vez). Luego escribe /newbot y env\u00edalo. As\u00ed le dices a BotFather que quieres crear un nuevo bot.',
        image: '/assets/activation/step2-newbot.png',
      },
      {
        num: '03',
        title: 'Dale un nombre a tu bot y elige un usuario.',
        body: 'BotFather te pregunta c\u00f3mo llamar a tu bot. Este es el nombre visible \u2014 el nombre amable que aparece arriba del chat. Puede tener espacios y may\u00fasculas. Escribe un nombre y env\u00edalo.\n\nLuego BotFather te pide un usuario. Este es \u00fanico en todo Telegram y debe terminar en `bot` (por ejemplo `mi_companero_bot`). Sin espacios \u2014 solo letras, n\u00fameros y guiones bajos. Si el que eliges ya est\u00e1 ocupado, Telegram te pedir\u00e1 otro.',
        image: '/assets/activation/step3-naming.png',
      },
      {
        num: '04',
        title: 'Copia tu token.',
        body: '\u00a1Listo! BotFather env\u00eda un mensaje de confirmaci\u00f3n que incluye tu token \u2014 una l\u00ednea larga de letras y n\u00fameros. T\u00f3calo una vez en el m\u00f3vil para copiarlo, o selecci\u00f3nalo y c\u00f3pialo en el ordenador.',
        image: '/assets/activation/step4-token.png',
      },
    ],
    act_warning_label: 'MANT\u00c9N TU TOKEN EN PRIVADO',
    act_warning_body: 'Tu token funciona como una contrase\u00f1a para tu bot. Cualquiera que lo tenga puede controlarlo. Nunca lo publiques en abierto ni lo compartas en capturas o chats \u2014 difum\u00ednalo en cualquier captura que reutilices. Si alguna vez se expone, vuelve a BotFather, env\u00eda /revoke y recibir\u00e1s uno nuevo.',
    act_last_label: '\u00daLTIMO PASO',
    act_last_h: 'P\u00e9galo en',
    act_last_h_accent: 'evern.',
    act_last_body: 'Vuelve a tu configuraci\u00f3n de evern y pega el token en el campo del token. Eso es todo \u2014 tu compa\u00f1ero ya est\u00e1 conectado con tu propio bot de Telegram y listo para hablar.',
    act_help_pre: '\u00bfNecesitas ayuda? Escr\u00edbenos a',
    act_back: 'Volver al inicio',

    // Shared legal/policy
    legal_back: 'Volver al inicio',
    legal_effective: 'En vigor desde el 22 de mayo de 2026',
    legal_contact_pre: '\u00bfDudas sobre esta pol\u00edtica? Escr\u00edbenos a',
    legal_email: 'hello@heisenbergdynamics.com',

    // Privacy Policy
    pp_eyebrow: 'POL\u00cdTICA DE PRIVACIDAD',
    pp_h: 'Privacidad,',
    pp_h_accent: 'dicha sin rodeos.',
    pp_intro: 'Esta pol\u00edtica explica, en lenguaje claro, qu\u00e9 datos guarda Heisenberg, por qu\u00e9 los guarda, qu\u00e9 nunca hacemos con ellos y los derechos que tienes sobre ellos. Aplica a todas las personas que usan nuestro servicio, est\u00e9n donde est\u00e9n.',
    pp_sections: [
      {
        title: 'Qui\u00e9nes somos.',
        body: [
          'Heisenberg es un servicio operado por Heisenberg Dynamics, con domicilio registrado en 5830 E 2nd St, Ste 7000 #23006, Casper, Wyoming 82609, US.',
          'Para cualquier consulta, solicitud o duda sobre privacidad, escr\u00edbenos a hello@heisenbergdynamics.com. Una persona real lee cada mensaje.',
        ],
      },
      {
        title: 'Qu\u00e9 datos recogemos.',
        body: [
          'Informaci\u00f3n de cuenta que aportas al registrarte \u2014 como tu correo y las decisiones que tomas para configurar a tu compa\u00f1ero.',
          'El contenido de tus conversaciones: mensajes que env\u00edas y recibes, notas y grabaciones de voz, fotos, capturas y documentos que compartes con tu compa\u00f1ero.',
          'Informaci\u00f3n que tu compa\u00f1ero re\u00fane de la web a petici\u00f3n tuya, al responder preguntas o realizar tareas por ti.',
          'Datos b\u00e1sicos de uso y t\u00e9cnicos necesarios para mantener el servicio en marcha \u2014 por ejemplo, cu\u00e1ndo recibi\u00f3 tu compa\u00f1ero el \u00faltimo mensaje, o si una funci\u00f3n fall\u00f3.',
        ],
      },
      {
        title: 'C\u00f3mo los usamos.',
        body: [
          'Para hacer funcionar a tu compa\u00f1ero: enviar respuestas, mantener conversaciones y personalizar la experiencia seg\u00fan lo que has elegido compartir.',
          'Para mantener la memoria que has construido con tu compa\u00f1ero, de modo que recuerde lo que te importa con el paso del tiempo.',
          'Para procesar la voz \u2014 transcribiendo lo que dices y generando respuestas habladas con la voz de tu compa\u00f1ero.',
          'Para que tu compa\u00f1ero te busque por su cuenta cuando hay algo que merece aparecer.',
          'Para darte soporte cuando nos escribes, y para gestionar el cobro en los planes de pago.',
        ],
      },
      {
        title: 'Bases legales para el tratamiento.',
        body: [
          'Cuando la ley aplicable lo exige (por ejemplo, para usuarios en la Uni\u00f3n Europea), nos basamos en: tu consentimiento (que puedes retirar en cualquier momento), la ejecuci\u00f3n del contrato contigo (para que el servicio realmente funcione) y nuestros intereses leg\u00edtimos en operar, asegurar y mejorar el servicio sin sobrepasar tus derechos.',
        ],
      },
      {
        title: 'Lo que no hacemos.',
        body: [
          'No vendemos tus datos. Nunca.',
          'No te mostramos anuncios y no compartimos tus datos con anunciantes ni intermediarios de datos.',
          'No usamos los datos de una persona para entrenar la experiencia de otra. Tus conversaciones se quedan con tu compa\u00f1ero.',
          'No leemos tu memoria privada m\u00e1s all\u00e1 de lo necesario para operar el servicio \u2014 y respetamos las solicitudes de borrado.',
        ],
      },
      {
        title: 'Proveedores y a qui\u00e9n damos acceso.',
        body: [
          'Para operar el servicio, usamos un peque\u00f1o n\u00famero de proveedores externos, estrictamente para que Heisenberg funcione. Los mencionamos aqu\u00ed por categor\u00eda:',
          'Proveedores de hosting e infraestructura, que almacenan y sirven las partes t\u00e9cnicas del servicio.',
          'Proveedores de procesamiento de IA y voz, que procesan tus mensajes, tu voz y otros contenidos para generar las respuestas de tu compa\u00f1ero.',
          'Un procesador de pagos (Stripe), que se encarga de los pagos en los planes de pago. Tus datos de tarjeta se introducen directamente en Stripe y nosotros no los guardamos.',
          'La plataforma de mensajer\u00eda (Telegram), a trav\u00e9s de la cual t\u00fa y tu compa\u00f1ero intercambian mensajes, notas de voz y archivos.',
          'Cada proveedor solo trata tus datos para cumplir su funci\u00f3n. No vendemos tus datos a terceros.',
        ],
      },
      {
        title: 'Cu\u00e1nto tiempo guardamos tus datos.',
        body: [
          'Guardamos tus datos mientras tu cuenta est\u00e9 activa.',
          'Cuando cierras tu cuenta, eliminamos tus datos en un plazo de 30 d\u00edas, salvo los registros que estemos obligados a conservar m\u00e1s tiempo por razones legales o de facturaci\u00f3n (por ejemplo, facturas que debemos mantener por motivos fiscales).',
        ],
      },
      {
        title: 'Seguridad.',
        body: [
          'Los datos que viajan entre t\u00fa y el servicio se cifran en tr\u00e1nsito.',
          'El acceso a los sistemas que guardan datos de usuarios est\u00e1 limitado a las pocas personas que lo necesitan para hacer su trabajo.',
          'Cada usuario corre en su propio entorno privado \u2014 tus datos no se mezclan con los de nadie m\u00e1s.',
          'Mantenemos salvaguardas internas y revisamos nuestras pr\u00e1cticas de forma continua. Si alguna vez ocurriera una brecha que afectase a tus datos, te lo comunicaremos.',
        ],
      },
      {
        title: 'Tus derechos.',
        body: [
          'Tienes derecho a acceder a los datos que tenemos sobre ti, a corregirlos, a borrarlos, a exportarlos en un formato portable, a retirar el consentimiento cuando nos hayamos apoyado en \u00e9l, y a oponerte a ciertos tipos de tratamiento.',
          'Para ejercer cualquiera de estos derechos, escribe a hello@heisenbergdynamics.com y te responderemos sin demora.',
        ],
      },
      {
        title: 'Transferencias internacionales.',
        body: [
          'El servicio se opera desde Estados Unidos, y tus datos pueden tratarse en pa\u00edses distintos del tuyo. Cuando esto ocurre, aplicamos las garant\u00edas adecuadas para proteger tus datos conforme a la ley aplicable.',
        ],
      },
      {
        title: 'Menores.',
        body: [
          'Heisenberg no est\u00e1 dirigido a personas menores de 16 a\u00f1os. No recogemos a sabiendas datos de menores de 16. Si crees que un menor nos ha facilitado datos personales, escribe a hello@heisenbergdynamics.com y los borraremos.',
        ],
      },
      {
        title: 'Cookies y seguimiento.',
        body: [
          'Nuestro sitio web no usa cookies de seguimiento, anal\u00edtica ni rastreadores de terceros. No te seguimos por la web.',
        ],
      },
      {
        title: 'Cambios en esta pol\u00edtica.',
        body: [
          'Si actualizamos esta pol\u00edtica, cambiaremos la fecha de entrada en vigor de arriba y \u2014 ante cambios sustanciales \u2014 te avisaremos a trav\u00e9s del servicio antes de que entren en vigor.',
        ],
      },
    ],

    // Terms & Conditions
    tc_eyebrow: 'T\u00c9RMINOS Y CONDICIONES',
    tc_h: 'Los t\u00e9rminos,',
    tc_h_accent: 'en lenguaje claro.',
    tc_intro: 'Estos son los t\u00e9rminos que aplican cuando usas Heisenberg. Hemos intentado escribirlos en un lenguaje claro y humano. Al usar el servicio, aceptas estos t\u00e9rminos.',
    tc_sections: [
      {
        title: 'Aceptaci\u00f3n de los t\u00e9rminos.',
        body: [
          'Al crear una cuenta, iniciar sesi\u00f3n o usar Heisenberg de cualquier otra forma, aceptas estos t\u00e9rminos. Si no est\u00e1s de acuerdo, por favor no uses el servicio.',
        ],
      },
      {
        title: 'Qu\u00e9 es Heisenberg.',
        body: [
          'Heisenberg es un compa\u00f1ero de IA personal al que se accede a trav\u00e9s de Telegram. Seg\u00fan tu plan, ofrece chat de texto, notas de voz, conversaciones de voz en vivo, memoria a largo plazo, recordatorios, investigaci\u00f3n web y \u2014 en los planes superiores \u2014 actuaciones en tu nombre para ciertas tareas.',
          'Las funciones disponibles var\u00edan seg\u00fan el plan; con el tiempo podemos a\u00f1adir, modificar o retirar capacidades.',
        ],
      },
      {
        title: 'Elegibilidad y tu cuenta.',
        body: [
          'Debes tener al menos 16 a\u00f1os para usar Heisenberg.',
          'Aceptas aportar informaci\u00f3n veraz al registrarte y mantenerla actualizada.',
          'Eres responsable de tu propia cuenta de Telegram y del token del bot que conectas a Heisenberg. Trata el token como una contrase\u00f1a \u2014 quien lo tenga puede controlar tu bot.',
        ],
      },
      {
        title: 'Uso aceptable.',
        body: [
          'Aceptas no usar el servicio para nada ilegal, abusivo, ni que infrinja los derechos de otras personas.',
          'Aceptas no acosar ni amenazar a otros a trav\u00e9s del servicio, no intentar manipular o usar mal la IA para producir contenido da\u00f1ino, y no intentar saltarte las medidas de seguridad.',
          'Podemos suspender o cerrar las cuentas que incumplan estas reglas.',
        ],
      },
      {
        title: 'Lo que el compa\u00f1ero puede y no puede hacer.',
        body: [
          'Tu compa\u00f1ero puede equivocarse y a veces puede producir informaci\u00f3n inexacta o incompleta. Verifica cualquier cosa importante antes de basarte en ella.',
          'Heisenberg no ofrece asesoramiento profesional legal, m\u00e9dico, financiero u otro tipo regulado. Si lo necesitas, consulta a un profesional cualificado.',
        ],
      },
      {
        title: 'Suscripciones, cobros y planes.',
        body: [
          'Heisenberg ofrece un nivel gratuito y niveles de pago.',
          'Las suscripciones de pago se cobran de forma recurrente a trav\u00e9s de Stripe, nuestro procesador de pagos. El precio actual y las funciones de cada plan se muestran al pagar.',
          'Los precios y las funciones pueden cambiar con el tiempo. Si cambian, te lo avisaremos con antelaci\u00f3n razonable; el precio que viste al pagar aplica al periodo de facturaci\u00f3n en curso.',
        ],
      },
      {
        title: 'Cancelaci\u00f3n y reembolsos.',
        body: [
          'Puedes cancelar una suscripci\u00f3n de pago en cualquier momento desde el servicio o escribiendo a hello@heisenbergdynamics.com. Al cancelar, mantienes el acceso hasta el final del periodo que ya hayas pagado.',
          'Las suscripciones no son reembolsables salvo que la ley aplicable exija un reembolso.',
        ],
      },
      {
        title: 'Propiedad intelectual.',
        body: [
          'Heisenberg es propietaria del servicio, del software, de la marca y del contenido que publica.',
          'T\u00fa eres due\u00f1o del contenido que aportas: tus mensajes, notas de voz, fotos y la memoria que construyes con tu compa\u00f1ero. Concedes a Heisenberg una licencia limitada para tratar ese contenido con el \u00fanico fin de proporcionarte el servicio.',
        ],
      },
      {
        title: 'Plataformas de terceros.',
        body: [
          'El servicio se apoya en Telegram y en otros proveedores externos. Estas plataformas son operadas de forma independiente. No somos responsables de sus ca\u00eddas, cambios o acciones, aunque haremos lo posible por mantener el servicio funcionando mientras est\u00e9n disponibles.',
        ],
      },
      {
        title: 'Garant\u00edas y l\u00edmite de responsabilidad.',
        body: [
          'El servicio se presta "tal cual" y "seg\u00fan disponibilidad", sin garant\u00edas de ning\u00fan tipo en la m\u00e1xima medida permitida por la ley.',
          'En la m\u00e1xima medida permitida por la ley, Heisenberg no ser\u00e1 responsable de da\u00f1os indirectos, incidentales, especiales o consecuentes, ni de la p\u00e9rdida de datos, beneficios o negocio.',
          'Algunas de estas limitaciones pueden no aplicarse a ti si vives en un lugar (por ejemplo, la Uni\u00f3n Europea) donde ciertas limitaciones no son v\u00e1lidas.',
        ],
      },
      {
        title: 'Indemnizaci\u00f3n.',
        body: [
          'Aceptas indemnizar y mantener indemne a Heisenberg frente a reclamaciones derivadas de tu uso indebido del servicio, de tu incumplimiento de estos t\u00e9rminos o de la vulneraci\u00f3n de derechos de otra persona.',
        ],
      },
      {
        title: 'Terminaci\u00f3n.',
        body: [
          'Puedes dejar de usar el servicio en cualquier momento y cerrar tu cuenta.',
          'Podemos suspender o cancelar tu acceso si incumples estos t\u00e9rminos o si lo exigen motivos legales u operativos. Cuando sea razonable, te explicaremos por qu\u00e9.',
        ],
      },
      {
        title: 'Ley aplicable.',
        body: [
          'Estos t\u00e9rminos se rigen por las leyes del Estado de Wyoming, Estados Unidos, sin considerar sus normas de conflicto de leyes. Las disputas se resolver\u00e1n ante los tribunales situados en Wyoming, salvo que la ley aplicable exija otra cosa.',
        ],
      },
      {
        title: 'Cambios en estos t\u00e9rminos.',
        body: [
          'Podemos actualizar estos t\u00e9rminos cada cierto tiempo. Si lo hacemos, cambiaremos la fecha de entrada en vigor de arriba y, ante cambios sustanciales, te avisaremos a trav\u00e9s del servicio antes de que entren en vigor.',
        ],
      },
      {
        title: 'Contacto.',
        body: [
          '\u00bfDudas sobre estos t\u00e9rminos? Escr\u00edbenos a hello@heisenbergdynamics.com.',
        ],
      },
    ],

    // Footer
    foot_cta_label: 'CONOCE EL TUYO',
    foot_h: 'empieza el tuyo.',
    foot_h_accent: '',
    foot_cta: 'conoce a tu compa\u00f1ero',
    foot_cta_url: 'https://subscription.evern.ai/',
    foot_copy: '\u00a9 2026 Heisenberg. Todos los derechos reservados.',
    foot_address: '5830 E 2nd St, Ste 7000 #23006, Casper, Wyoming 82609, US',
    foot_email: 'hello@heisenbergdx.com',
    foot_website: 'heisenbergdx.com',
    foot_website_url: 'https://heisenbergdx.com',
    foot_links: [
      { label: 'Privacidad', to: '/privacy-policy' },
      { label: 'T\u00e9rminos', to: '/terms' },
    ],
  },
};

export default translations;
